export const CLIENT_RELOAD_TARGET_KEY = 'ae-client-reload-target'
export const CLIENT_BUILD_META_SELECTOR = 'meta[name="aeppic-client-build"]'

type ClientUpdateDocument = {
  visibilityState: string
  addEventListener(type: 'visibilitychange', listener: () => void): void
  removeEventListener(type: 'visibilitychange', listener: () => void): void
  querySelector?(selector: string): { content?: string } | null
  documentElement?: { lang?: string }
  body?: HTMLElement
  createElement?: Document['createElement']
}

export type ClientUpdateEnvironment = {
  document: ClientUpdateDocument
  sessionStorage: Pick<Storage, 'getItem' | 'setItem'>
  location: Pick<Location, 'reload'>
  /** Shows a non-blocking notice; `reload` reloads at once. */
  showBanner?(reload: () => void): void
}

export type ClientReloadRequest = {
  /** Why the client is outdated, e.g. `deploy` or `local-model`. */
  reason: string
  /** What the reload should reach, e.g. the server's client build id. */
  target: string
}

/** The client build this page was loaded with, from the server-rendered index.html. */
export function loadedClientBuild(document?: Pick<ClientUpdateDocument, 'querySelector'>): string|null {
  return document?.querySelector?.(CLIENT_BUILD_META_SELECTOR)?.content || null
}

/**
 * Reloads an outdated tab without interrupting the user: a hidden tab reloads
 * at once, a visible one shows a banner and reloads when the user switches
 * away. `sessionStorage` remembers the target, so a tab that is still
 * outdated after reloading (a stale cached index.html) only keeps the banner.
 */
export class ClientUpdate {
  private _scheduledTarget: string|null = null
  private _bannerShown = false

  constructor(private _environment: ClientUpdateEnvironment = globalThis as unknown as ClientUpdateEnvironment) {}

  requestReload({ reason, target }: ClientReloadRequest): 'reloading'|'scheduled'|'suppressed' {
    const { document, sessionStorage } = this._environment
    if (this._scheduledTarget === target) return 'scheduled'
    if (sessionStorage.getItem(CLIENT_RELOAD_TARGET_KEY) === target) {
      console.warn(`[client-update] Still outdated after reloading for ${target} (${reason}); waiting for a manual reload`)
      this._showBanner(target)
      return 'suppressed'
    }
    if (document.visibilityState === 'hidden') {
      this._reload(target)
      return 'reloading'
    }
    this._scheduledTarget = target
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return
      document.removeEventListener('visibilitychange', onVisibilityChange)
      this._reload(target)
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    this._showBanner(target)
    return 'scheduled'
  }

  private _reload(target: string) {
    this._environment.sessionStorage.setItem(CLIENT_RELOAD_TARGET_KEY, target)
    this._environment.location.reload()
  }

  private _showBanner(target: string) {
    if (this._bannerShown) return
    this._bannerShown = true
    const reload = () => this._reload(target)
    if (this._environment.showBanner) this._environment.showBanner(reload)
    else showDefaultBanner(this._environment.document, reload)
  }
}

function showDefaultBanner(document: ClientUpdateDocument, reload: () => void) {
  if (!document.createElement || !document.body) return
  const german = /^de\b/i.test(document.documentElement?.lang || globalThis.navigator?.language || '')
  const banner = document.createElement('div')
  banner.setAttribute('role', 'status')
  banner.setAttribute('data-aeppic-client-update', '')
  banner.style.cssText = [
    'position:fixed', 'left:50%', 'bottom:16px', 'transform:translateX(-50%)', 'z-index:2147483647',
    'display:flex', 'gap:12px', 'align-items:center', 'max-width:calc(100% - 32px)',
    'padding:10px 14px', 'border-radius:8px', 'background:#1f2937', 'color:#fff',
    'font:14px/1.4 system-ui,sans-serif', 'box-shadow:0 4px 16px rgba(0,0,0,.25)',
  ].join(';')
  const text = document.createElement('span')
  text.textContent = german
    ? 'Eine neue Version ist verfügbar. Die Seite lädt neu, sobald Sie zu einem anderen Tab wechseln.'
    : 'A new version is available. The page reloads when you switch to another tab.'
  const button = document.createElement('button')
  button.type = 'button'
  button.textContent = german ? 'Jetzt neu laden' : 'Reload now'
  button.style.cssText = 'padding:4px 10px;border:0;border-radius:6px;background:#fff;color:#1f2937;cursor:pointer;font:inherit'
  button.addEventListener('click', reload)
  banner.append(text, button)
  document.body.append(banner)
}
